
import React, { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvatarUpload } from "@/components/upload/AvatarUpload";
import Header from "@/components/shared/Header";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { User, Mail, Calendar, Shield } from "lucide-react";
import { profileUpdateSchema } from "@/lib/profileValidation";

const Profile: React.FC = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [optimisticData, setOptimisticData] = useState<any>(null); // For optimistic UI rollback
  const [formData, setFormData] = useState({
    username: profile?.username || "",
    full_name: profile?.full_name || "",
    bio: profile?.bio || "",
    avatar_url: profile?.avatar_url || "",
  });
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [checkingUsername, setCheckingUsername] = useState(false);

  // When profile changes, update local form
  React.useEffect(() => {
    setFormData({
      username: profile?.username || "",
      full_name: profile?.full_name || "",
      bio: profile?.bio || "",
      avatar_url: profile?.avatar_url || "",
    });
  }, [profile]);

  // Check for username duplicate (onBlur/after edit)
  const checkUsernameAvailable = async (username: string) => {
    if (!username || username.length < 3) return;
    setCheckingUsername(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", username)
        .neq("id", user?.id)
        .maybeSingle();
      if (!error && data) {
        setFieldErrors((prev) => ({
          ...prev,
          username: "That username is already taken",
        }));
        return false;
      }
      setFieldErrors((prev) => ({ ...prev, username: "" }));
      return true;
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        username: "Failed to check username availability.",
      }));
      return false;
    } finally {
      setCheckingUsername(false);
    }
  };

  // Zod validation
  const validateForm = () => {
    const result = profileUpdateSchema.safeParse(formData);
    if (!result.success) {
      const errors: { [key: string]: string } = {};
      for (const err of result.error.issues) {
        errors[err.path[0] as string] = err.message;
      }
      setFieldErrors(errors);
      return false;
    }
    setFieldErrors({});
    return true;
  };

  // Optimistic profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    if (!user) return;

    // Validate form fields
    if (!validateForm()) return;

    // Check username duplicate before actual submit
    const usernameOk = await checkUsernameAvailable(formData.username);
    if (usernameOk === false) return;

    setOptimisticData({ ...profile, ...formData }); // snapshot for rollback
    setLoading(true);

    // Optimistic update: show new UI immediately
    setFormData({ ...formData });

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          username: formData.username,
          full_name: formData.full_name,
          bio: formData.bio,
          avatar_url: formData.avatar_url,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        description: "Profile updated successfully.",
        variant: "default",
      });

      // Ensure latest state (real-time sync)
      await refreshProfile();
    } catch (error: any) {
      // Roll back UI on error
      setFormData({
        username: profile?.username || "",
        full_name: profile?.full_name || "",
        bio: profile?.bio || "",
        avatar_url: profile?.avatar_url || "",
      });
      toast({
        description: "Failed to update profile.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (url: string | null) => {
    setFormData((prev) => ({ ...prev, avatar_url: url || "" }));
    setFieldErrors((prev) => ({ ...prev, avatar_url: "" }));
  };

  const displayName =
    formData.full_name ||
    formData.username ||
    user?.email?.split("@")[0] ||
    "User";

  // Fallback: use initials, else random bg emoji/color
  const initials = displayName
    .split(" ")
    .map((name) => name[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div>
            <h1 className="text-3xl font-bold">Profile</h1>
            <p className="text-black mt-1">
              Manage your profile information and preferences
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Overview */}
            <div className="lg:col-span-1">
              <Card>
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <div className="flex justify-center">
                      <AvatarUpload
                        currentAvatarUrl={formData.avatar_url}
                        fallback={initials || "🌱"}
                        onAvatarChange={handleAvatarChange}
                        size="lg"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{displayName}</h3>
                      <p className="text-black">
                        @{formData.username || "username"}
                      </p>
                      {profile?.role && (
                        <div className="flex items-center justify-center gap-1 mt-2">
                          <Shield className="h-4 w-4" />
                          <span className="text-sm capitalize">
                            {profile.role}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">Account Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-black" />
                    <span className="text-sm">{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-black" />
                    <span className="text-sm">
                      Joined {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-black" />
                    <span className="text-sm capitalize">
                      {profile?.role || "Reader"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Profile Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Edit Profile</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        value={formData.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        onBlur={() => checkUsernameAvailable(formData.username)}
                        placeholder="Enter your username"
                        maxLength={50}
                      />
                      {fieldErrors.username && (
                        <div className="text-sm text-red-500 ml-1 mt-1">
                          {fieldErrors.username}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            full_name: e.target.value,
                          })
                        }
                        placeholder="Enter your full name"
                        maxLength={100}
                      />
                      {fieldErrors.full_name && (
                        <div className="text-sm text-red-500 ml-1 mt-1">
                          {fieldErrors.full_name}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        value={formData.bio}
                        onChange={(e) =>
                          setFormData({ ...formData, bio: e.target.value })
                        }
                        placeholder="Tell us about yourself"
                        rows={4}
                        maxLength={500}
                      />
                      {fieldErrors.bio && (
                        <div className="text-sm text-red-500 ml-1 mt-1">
                          {fieldErrors.bio}
                        </div>
                      )}
                    </div>

                    <Button type="submit" disabled={loading || checkingUsername}>
                      {loading ? "Updating..." : "Update Profile"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
